import { useEffect, useState, useRef } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Bell, BellRing, BellOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { log } from "@/lib/logger";
import { isPushSupported, getPushStatus, subscribePush, unsubscribePush, testPush } from "@/lib/webpush";

export default function NotificationBell() {
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushState, setPushState] = useState({ supported: false, granted: false, subscribed: false });
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await http.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // Notifications are a background poll; a failed fetch is not user-facing,
      // but log it so we can spot outages during dev.
      log.warn("[notifications] load failed:", e?.message || e);
    }
  };

  const refreshPush = async () => {
    if (!isPushSupported()) { setPushState({ supported: false, granted: false, subscribed: false }); return; }
    setPushState(await getPushStatus());
  };

  useEffect(() => {
    if (!user) return;
    load();
    refreshPush();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, [user]);

  if (!user) return null;

  const markAllRead = async () => {
    await http.post("/notifications/read-all");
    load();
  };

  const clickItem = async (n) => {
    if (!n.read) await http.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
    load();
  };

  const togglePush = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (pushState.subscribed) {
        await unsubscribePush();
        toast.success(lang === "fr" ? "Notifications désactivées" : "Notifications disabled");
      } else {
        await subscribePush();
        toast.success(lang === "fr" ? "Notifications activées 🚀" : "Notifications enabled 🚀");
      }
      await refreshPush();
    } catch (e) {
      toast.error(e?.message || (lang === "fr" ? "Impossible d'activer les notifications" : "Cannot enable notifications"));
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    try {
      await testPush();
      toast.success(lang === "fr" ? "Test envoyé" : "Test sent");
      load();
    } catch (e) {
      // Test push may be unavailable (VAPID keys missing, permission denied);
      // surface a friendly error instead of failing silently.
      toast.error(lang === "fr" ? "Test impossible" : "Test failed");
      log.warn("[notifications] test push failed:", e?.message || e);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative rounded-full p-2 hover:bg-slate-100 transition-colors">
          <Bell className="w-5 h-5 text-slate-600" />
          {unread > 0 && (
            <span data-testid="notif-count" className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#F97316] text-white text-[9px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <p className="text-sm font-semibold">{lang === "fr" ? "Notifications" : "Notifications"}</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-[#1E3A8A] hover:underline">
              {lang === "fr" ? "Tout marquer lu" : "Mark all read"}
            </button>
          )}
        </div>
        {pushState.supported && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              {pushState.subscribed
                ? <BellRing className="w-4 h-4 text-[#16A34A] shrink-0" />
                : <BellOff className="w-4 h-4 text-slate-400 shrink-0" />}
              <p className="text-xs text-slate-600 truncate">
                {pushState.subscribed
                  ? (lang === "fr" ? "Alertes navigateur ON" : "Browser alerts ON")
                  : (lang === "fr" ? "Recevoir les alertes sur ce navigateur" : "Get alerts on this browser")}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {pushState.subscribed && (
                <button data-testid="push-test-btn" onClick={runTest} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:bg-white">
                  {lang === "fr" ? "Test" : "Test"}
                </button>
              )}
              <button
                data-testid="push-toggle-btn"
                disabled={busy}
                onClick={togglePush}
                className={`text-[11px] font-semibold px-3 py-1 rounded-full ${pushState.subscribed ? "bg-slate-200 text-slate-700" : "bg-[#F97316] text-white hover:bg-[#EA580C]"}`}
              >
                {pushState.subscribed
                  ? (lang === "fr" ? "Désactiver" : "Disable")
                  : (lang === "fr" ? "Activer" : "Enable")}
              </button>
            </div>
          </div>
        )}
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">
              {lang === "fr" ? "Aucune notification" : "No notifications"}
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => clickItem(n)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${!n.read ? "bg-[#FFF7ED]" : ""}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{n.title}</p>
                  <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
